import Papa from "papaparse";
import {
  Card,
  Employee,
  EmployeeAccount,
  Plan,
  ServiceSector,
  Tag,
  TagCategory
} from "../models";
import { Transaction } from "../models/transactions/transaction";
import { TransactionStatus } from "../models/transactions/transactionStatus";
import { Charge } from "../models/transactions/charge";
import { EmployerFundLedger } from "../models/funding/employerFundLedger";
import { formatCurrency } from "./formatters";
import { resolveTransactionPlanNames } from "./resolveTransactionPlanNames";

const formatDate = (dateInput: Date | string) => {
  const date = new Date(dateInput);

  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const year = date.getUTCFullYear();

  return `${day}-${month}-${year}`;
};

const convertStringToTitleCase = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const convertCamelToTitleCase = (camelCaseString: string): string => {
  return camelCaseString
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());
};

const getPlanAndTierName = (
  planId: string,
  tierId: string,
  plans: Plan[] | undefined
) => {
  const plan = plans?.find((p) => p.id === planId);
  const tier = plan?.tiers.find((t) => t.id === tierId);
  if (!plan || !tier) return;

  return `${plan.name} - ${tier.name}`;
};

const getTagColumns = (
  employeeTags: Tag[] | undefined,
  tagCategories: TagCategory[] | undefined
): { [key: string]: string | null } => {
  if (!tagCategories) return {};

  const result: { [key: string]: string | null } = tagCategories.reduce(
    (acc, category) => {
      acc[`tag[${category.name}]`] = null;
      return acc;
    },
    {} as { [key: string]: string | null }
  );

  if (employeeTags) {
    for (const tag of employeeTags) {
      const category = tagCategories.find((tc) => tc.id === tag.tagCategoryId);
      if (category) {
        result[`tag[${category.name}]`] = tag.name;
      }
    }
  }

  return result;
};

const joinEmployeeData = (
  employeeId: string,
  isAnonymised: boolean,
  employees: Employee[],
  tagCategories: TagCategory[] | undefined
) => {
  const employee = employees?.find((e) => e.id === employeeId);
  return {
    ...(isAnonymised
      ? {}
      : {
          firstName: employee?.firstName,
          lastName: employee?.lastName,
          email: employee?.contact.email
        }),
    customerEmployeeId: employee?.customerEmployeeId,
    employeeStatus: employee?.status,
    ...getTagColumns(employee?.tags, tagCategories)
  };
};

export const convertObjectKeysToTitleCase = (
  obj: Record<string, any>
): Record<string, any> => {
  const newObj: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[convertCamelToTitleCase(key)] = obj[key];
    }
  }
  return newObj;
};

export const convertObjectKeysToSnakeCase = (
  obj: Record<string, any>
): Record<string, any> => {
  const newObj: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()] = obj[key];
    }
  }
  return newObj;
};

export const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const convertEmployeesToCsv = (
  employees: Employee[],
  accounts: EmployeeAccount[],
  tagCategories: TagCategory[] | undefined,
  plans: Plan[] | undefined
): string => {
  const getSpend = (accounts: EmployeeAccount[]) => {
    const availableBalance = accounts?.reduce(
      (accountSum, account) =>
        accountSum + (account.availableBalance?.amount ?? 0),
      0
    );

    const startingBalance = accounts?.reduce(
      (balanceSum, account) =>
        balanceSum + (account?.startingBalance.amount ?? 0),
      0
    );

    return { availableBalance, startingBalance };
  };

  const csvData = employees?.map((employee) => {
    const { firstName, lastName, contact, tags, cards, status, createdAt } =
      employee;
    const employeeAccounts = accounts.filter(
      (x) => x.employeeId === employee.id
    );

    const { availableBalance, startingBalance } = getSpend(employeeAccounts);

    const csvRow = convertObjectKeysToTitleCase({
      firstName,
      lastName,
      email: contact?.email,
      phone: contact?.phone,
      availableBalance: formatCurrency(availableBalance).replaceAll(",", ""),
      startingBalance: formatCurrency(startingBalance).replaceAll(",", ""),
      plans: employeeAccounts
        ?.map((a) => getPlanAndTierName(a.planId, a.tierId, plans))
        .toString()
        .replaceAll(",", " & "),
      cardStatus:
        cards && cards.length ? cards[cards?.length - 1]?.status : "N/A", // Latest card status
      status,
      createdAt: formatDate(createdAt),
      customerEmployeeId: employee?.customerEmployeeId,
      ...getTagColumns(tags, tagCategories)
    });

    return csvRow;
  });

  const csv = Papa.unparse(csvData);
  return csv;
};

export const convertTransactionsToCsv = (
  transactions: Transaction[],
  isAnonymised: boolean,
  serviceSectors: ServiceSector[] | undefined,
  employees: Employee[] | undefined,
  tagCategories: TagCategory[] | undefined,
  plans: Plan[] | undefined,
  accounts: { id: string; name: string }[] | undefined
): string => {
  // Get all unique account names from all transactions' charges
  const uniqueAccountIds = new Set<string>();
  transactions.forEach(transaction => {
    transaction.charges?.forEach(charge => {
      if (charge.accountId) {
        uniqueAccountIds.add(charge.accountId);
      }
    });
  });

  // Convert account IDs to names and sort them
  const accountNames = Array.from(uniqueAccountIds)
    .map(id => accounts?.find(a => a.id === id)?.name)
    .filter((name): name is string => !!name)
    .sort();

  const csvData = transactions?.map((transaction) => {
    const {
      employeeId,
      cardAcceptor,
      billingAmount,
      status,
      serviceSectorId,
      createdAt,
      charges
    } = transaction;

    const merchant = cardAcceptor
      ? convertStringToTitleCase(cardAcceptor.split(/\s{2,}/)[0])
      : null;

    // Create a map of account name to charge amount
    const chargesByAccount = new Map<string, number>();
    charges?.forEach(charge => {
      const accountName = accounts?.find(a => a.id === charge.accountId)?.name;
      if (accountName) {
        chargesByAccount.set(accountName, charge.amount.amount);
      }
    });

    // Create charge columns for each unique account
    const chargeColumns: { [key: string]: string } = {};
    accountNames.forEach(accountName => {
      const amount = chargesByAccount.get(accountName);
      chargeColumns[`${accountName}`] = amount ? formatCurrency(amount).replaceAll(",", "") : '';
    });

    const csvRow = convertObjectKeysToTitleCase({
      merchant: merchant?.replaceAll(",", ""),
      amount: formatCurrency(billingAmount?.amount).replaceAll(",", ""),
      status,
      serviceSector: serviceSectors
        ?.find((ss) => ss.id === serviceSectorId)
        ?.name?.replaceAll(",", ""),
      timestamp: formatDate(createdAt),
      account: resolveTransactionPlanNames(transaction, plans),
      ...chargeColumns,
      ...joinEmployeeData(
        employeeId,
        isAnonymised,
        employees ?? [],
        tagCategories ?? []
      )
    });

    return csvRow;
  });

  const csv = Papa.unparse(csvData);
  return csv;
};

// We omit non-approved transactions from this report
export const convertTransactionsToTaxLabFbtCsv = (
  transactions: Transaction[],
  employees: Employee[] | undefined,
  tagCategories: TagCategory[] | undefined,
  plans: Plan[] | undefined,
  accounts: { id: string; name: string }[] | undefined
): string => {
  // Get all unique account names from all transactions' charges
  const uniqueAccountIds = new Set<string>();
  transactions.forEach(transaction => {
    transaction.charges?.forEach(charge => {
      if (charge.accountId) {
        uniqueAccountIds.add(charge.accountId);
      }
    });
  });

  // Convert account IDs to names and sort them
  const accountNames = Array.from(uniqueAccountIds)
    .map(id => accounts?.find(a => a.id === id)?.name)
    .filter((name): name is string => !!name)
    .sort();

  const csvData = transactions
    ?.filter((t) => t.status === TransactionStatus.APPROVED)
    .map((transaction) => {
      const { employeeId, cardAcceptor, billingAmount, createdAt, charges } =
        transaction;

      const employee = employees?.find((e) => e.id === employeeId);

      const merchant = cardAcceptor
        ? convertStringToTitleCase(cardAcceptor.split(/\s{2,}/)[0])
        : null;

      // Create a map of account name to charge amount
      const chargesByAccount = new Map<string, number>();
      charges?.forEach(charge => {
        const accountName = accounts?.find(a => a.id === charge.accountId)?.name;
        if (accountName) {
          chargesByAccount.set(accountName, charge.amount.amount);
        }
      });

      // Create charge columns for each unique account
      const chargeColumns: { [key: string]: string } = {};
      accountNames.forEach(accountName => {
        const amount = chargesByAccount.get(accountName);
        chargeColumns[`${accountName}`] = amount ? formatCurrency(amount).replaceAll(",", "") : '';
      });

      const csvRow = convertObjectKeysToSnakeCase({
        employeeId: employee?.customerEmployeeId,
        description: "Extraordinary Pay",
        firstName: employee?.firstName,
        surname: employee?.lastName,
        valueIncl: billingAmount?.amount,
        gstRate: "",
        datePaid: formatDate(createdAt),
        comment: merchant,
        ...chargeColumns,
        ...getTagColumns(employee?.tags, tagCategories)
      });

      return csvRow;
    });

  const csv = Papa.unparse(csvData);
  return csv;
};

export const convertCardsToCsv = (
  cards: Card[],
  isAnonymised: boolean,
  employees: Employee[] | undefined,
  tagCategories: TagCategory[] | undefined,
  plans: Plan[] | undefined
): string => {
  const csvData = cards?.map((card) => {
    const { employeeId, maskedCardNumber, status, createdAt } = card;

    const csvRow = convertObjectKeysToTitleCase({
      maskedCardNumber,
      status,
      createdAt: formatDate(createdAt),
      ...joinEmployeeData(
        employeeId,
        isAnonymised,
        employees ?? [],
        tagCategories ?? []
      )
    });

    return csvRow;
  });

  const csv = Papa.unparse(csvData);
  return csv;
};

export const convertEmployerFundLedgersToStatementCsv = (
  ledgers: EmployerFundLedger[]
): string => {
  const csvData = ledgers?.map((ledger) => {
    const {
      type,
      createdAt,
      appliedAmount,
      transactionLedgerMetadata,
      employeeMetadata,
      issueCardMetadata,
      monthlyUserFeeMetadata
    } = ledger;

    const csvRow = convertObjectKeysToTitleCase({
      type: convertStringToTitleCase(type),
      amount: appliedAmount?.amount * -1,
      date: formatDate(createdAt),
      user: employeeMetadata
        ? employeeMetadata?.firstName + " " + employeeMetadata?.lastName
        : null,
      merchant: transactionLedgerMetadata?.cardAcceptor,
      transactionType: transactionLedgerMetadata?.type,
      category: transactionLedgerMetadata?.serviceSectorName,
      "Card Issue Type": issueCardMetadata?.cardIssueKind
        ? convertStringToTitleCase(issueCardMetadata.cardIssueKind)
        : null,
      "Card Type": issueCardMetadata?.cardOfferingType
        ? convertStringToTitleCase(issueCardMetadata.cardOfferingType)
        : null,

        "Module Fee Type": monthlyUserFeeMetadata 
        ? convertStringToTitleCase(monthlyUserFeeMetadata.employerModuleType)
        : null,
    });

    return csvRow;
  });

  const csv = Papa.unparse(csvData);
  return csv;
};
